import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  Signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { getEmployeeById, selectRouterParam } from '@fc-store/selectors';
import { ChatWsService } from '../services/chat-ws.service';
import { ChatChannelStore } from './chat-channel-store';
import {
  ChatBaseMessage,
  ChatMessage,
  RawChatMessage,
} from '../models/chat-message';
import { Employee } from '@fc-core/models';
import { MessageType } from '../models/message-type';
import { TokenData } from '../../auth/models/token-data';
import {
  decodeTokenData,
  EMPTY_TOKEN_DATA,
} from '../../auth/utils/token-utils';
import moment from 'moment-timezone';
import { ChatChannelsApiService } from '../services/chat-channels-api.service';
import { v4 as uuidv4 } from 'uuid';
import { Router } from '@angular/router';
import { Channel } from '../models/channel';
import { AmplitudeService } from '@fc-core/services/amplitude.service';
import { ChatAttachmentsApiService } from '../services/chat-attachments-api.service';
import { Op } from 'quill';
import { MessagePreview } from '../services/chat-links-api.service';
import { NotificationService } from '@fc-core/services/notification.service';
import { getErrorMessage } from '@fc-core/services/error-parsing';
import { HttpErrorResponse } from '@angular/common/http';
import { ChatMessagesStore } from './chat-messages-store';
import { ChatMessagesApiService } from '../services/chat-messages-api.service';
import { getUnmutedUnreadMessagesCount } from '../utils/chat-utils';
import { BotSearchService } from '../services/bot-search.service';
import { ChatNotificationDismissalService } from '../services/chat-notification-dismissal.service';

@Injectable({
  providedIn: 'root',
})
export class ChatFacadeService {
  tokenData: TokenData =
    decodeTokenData(localStorage.getItem('token')) ?? EMPTY_TOKEN_DATA;
  chatWs = inject(ChatWsService);
  channelStore = inject(ChatChannelStore);
  globalStore = inject(Store);
  router = inject(Router);
  messagesApi = inject(ChatMessagesApiService);
  channelsApi = inject(ChatChannelsApiService);
  attachmentsApi = inject(ChatAttachmentsApiService);
  notifications = inject(NotificationService);
  amplitude = inject(AmplitudeService);
  chatMessagesStore = inject(ChatMessagesStore);
  notificationDismissal = inject(ChatNotificationDismissalService);
  botSearch = inject(BotSearchService);

  currentChannelId = this.globalStore.selectSignal(
    selectRouterParam('channelId'),
  );

  currentChannel = computed(() =>
    this.channelStore.selectors
      .channels()
      .find((channel) => channel.id === this.currentChannelId()),
  );

  typingEvent = signal<{ channel: string; author: string } | null>(null);
  typingActive = computed(() => {
    const typingEvent = this.typingEvent();
    return (
      !!typingEvent &&
      typingEvent.author !== this.tokenData.employee_id &&
      typingEvent.channel === this.currentChannel()?.id
    );
  });
  messages: Signal<{ [channel: string]: ChatMessage[] }> =
    this.chatMessagesStore.selectors.messages;
  channels = this.channelStore.selectors.channels;
  online = this.channelStore.selectors.online;
  lastSeen = this.channelStore.selectors.lastSeen;
  typingDuration = 2000;
  maxTypingDuration = 10000;
  typingTimeoutId: any = null;
  private composerFocusRequestState = signal<{
    counter: number;
    text: string | null;
  }>({ counter: 0, text: null });
  composerFocusRequest = this.composerFocusRequestState.asReadonly();

  employeeInChannel = computed((): boolean => {
    const currentChannel = this.currentChannel();
    if (!currentChannel?.members?.length) return false;
    return currentChannel.members.some(
      (id: string) => id === this.tokenData.employee_id,
    );
  });

  requestComposerFocus(initialText?: string) {
    this.composerFocusRequestState.update((state) => ({
      counter: state.counter + 1,
      text: initialText ?? null,
    }));
  }

  private wasDisconnected = false;

  constructor() {
    this.chatWs.chatStream$.subscribe((message: ChatBaseMessage | null) =>
      this.processMessage(message),
    );

    effect(() => {
      if (!this.chatWs.wsConnected()) {
        this.wasDisconnected = true;
        return;
      }
      if (this.wasDisconnected) {
        this.wasDisconnected = false;
        // Unread counts only move via WS `unread` events, and the server
        // doesn't replay events missed while disconnected — refetch the
        // channel list so badges reconcile after every reconnect.
        this.channelStore.getChannels().catch(() => {
          // Keeps the last known list; the next reconnect refetches it.
        });
        this.chatMessagesStore.syncOnReconnect(this.currentChannelId() ?? null);
      }
    });
  }

  getOnlineEmployeeById = (id: string): Signal<boolean> => {
    return computed(() => this.online().includes(id));
  };

  getEmployeeById = (id: string) =>
    this.globalStore.selectSignal(getEmployeeById(id));

  getChannels = () => this.channelStore.getChannels();

  getChannelById = (channelId: string): Promise<Channel> => {
    const channels = this.channelStore.selectors.channels();
    const channel = channels?.find((channel) => channel.id === channelId);
    if (channel) return Promise.resolve(channel);
    return this.channelStore.getChannelById(channelId);
  };

  isChannelPinned = (channelId: string): boolean => {
    const channel = this.channelStore.selectors
      .channels()
      .find((storedChannel) => storedChannel.id === channelId);
    return channel?.config?.pinned === true;
  };

  async pinChannel() {
    await this.setChannelPinned(true);
  }

  async unpinChannel() {
    await this.setChannelPinned(false);
  }

  private async setChannelPinned(pinned: boolean) {
    const channelId = this.currentChannelId();
    if (!channelId) return;
    try {
      await this.channelStore.setChannelPinned(channelId, pinned);
      this.notifications.showSuccess(
        pinned
          ? 'Channel successfully pinned'
          : 'Channel successfully unpinned',
      );
    } catch {
      this.notifications.showError(
        pinned ? 'Error pinning channel.' : 'Error unpinning channel.',
      );
    }
  }

  async joinChannel(id?: string): Promise<Channel | undefined> {
    const channelId = id || this.currentChannelId();
    if (!channelId) return undefined;
    try {
      const channel = await this.channelsApi.joinChannel(channelId);
      this.channelStore.updateChannel(channel);
      return channel;
    } catch (error) {
      this.notifications.showError(getErrorMessage(error as HttpErrorResponse));
      throw error;
    }
  }

  async leaveChannel() {
    const channelId = this.currentChannelId();
    if (!channelId) return;
    await this.channelsApi.leaveChannel(channelId);
    // The channel list only holds channels the viewer is a member of, so a
    // left channel drops out of it entirely.
    this.router.navigate(['/chats']);
    this.channelStore.deleteChannel(channelId);
    this.chatMessagesStore.deleteMessages(channelId);
  }

  currentChannelMessages(): Signal<ChatMessage[]> {
    return computed(() => {
      const channelId = this.currentChannelId();
      if (!channelId) return [];
      const messages: ChatMessage[] =
        this.chatMessagesStore.selectors.messages()[channelId] || [];
      return messages;
    });
  }

  getOnlineMembers = (channel: Channel): Signal<Employee[]> =>
    computed(() =>
      channel.members
        .filter((member) => this.getOnlineEmployeeById(member)())
        .map((employeeId) => this.getEmployeeById(employeeId)())
        .filter((employee): employee is Employee => !!employee),
    );

  getOfflineMembers = (channel: Channel): Signal<Employee[]> =>
    computed(() =>
      channel.members
        .filter((member) => !this.getOnlineEmployeeById(member)())
        .map((employeeId) => this.getEmployeeById(employeeId)())
        .filter((employee): employee is Employee => !!employee),
    );

  getAllUnreadMessages = (): number =>
    getUnmutedUnreadMessagesCount(this.channelStore.selectors.channels());

  isMessageBelongToCurrentEmployee = (message: ChatMessage): boolean => {
    return message.author === this.tokenData.employee_id;
  };

  isMessageInvokedByCurrentEmployee = (message: ChatMessage): boolean =>
    message.invoker === this.tokenData.employee_id;

  isMessageUnread = (message: ChatMessage): boolean => {
    if (!this.isMessageBelongToCurrentEmployee(message)) {
      return !Array.from(message.meta?.viewed || []).some(
        (view) => view.employeeId === this.tokenData.employee_id,
      );
    }
    return false;
  };

  directChannelEmployee(channel: Channel): Signal<Employee | undefined> {
    const currentEmployeeId = this.tokenData.employee_id;
    const otherEmployeeId = channel.members.find(
      (member) => member !== currentEmployeeId,
    );
    if (!otherEmployeeId)
      return computed((): Employee | undefined => undefined);
    return this.getEmployeeById(otherEmployeeId);
  }

  async sendMessage(
    message: string,
    files: number[] = [],
    linkPreviews: MessagePreview[] = [],
    delta: Op[] | null = null,
    replyTo: string | null = null,
  ): Promise<void> {
    const messagePayload: RawChatMessage = {
      type: MessageType.MESSAGE,
      meta: {
        published: moment().toISOString(),
      },
      channel: this.currentChannelId(),
      content: {
        text: delta ?? [],
        attachments: files,
        previews: linkPreviews,
      },
      id: uuidv4(),
    };
    if (replyTo) {
      messagePayload.replyTo = replyTo;
    }
    this.chatWs.sendMessage(messagePayload);

    this.amplitude.track('chat_message_sent', {
      channel_id: this.currentChannelId(),
      message_id: messagePayload.id,
      message_length: message.length,
    });
  }

  createChannel(channel: Channel): Promise<Channel> {
    return this.channelsApi.createChannel(channel);
  }

  async updateChannel(channel: Channel): Promise<any> {
    const updatedChannel = await this.channelsApi.updateChannel(channel);
    this.channelStore.updateChannel(updatedChannel);
  }

  async deleteChannel() {
    const channelId = this.currentChannelId();
    if (!channelId) return;
    await this.channelsApi.deleteChannel(channelId);
    this.router.navigate(['/chats']);
    this.channelStore.deleteChannel(channelId);
    this.chatMessagesStore.deleteMessages(channelId);
  }

  async deleteAttachment(id: number) {
    await this.attachmentsApi.deleteFile(id);
  }

  private async processMessage(message: ChatBaseMessage | null) {
    if (!message) return;
    if (this.botSearch.receive(message)) {
      if (
        'channel' in message &&
        message.channel &&
        'id' in message &&
        typeof message.id === 'string'
      )
        this.chatMessagesStore.deleteMessage(message.id, message.channel);
      return;
    }

    if (message && (message as any)['errors']) {
      const errors = (message as any)['errors'][0];
      this.notifications.showError(errors.field + ' ' + errors.text);
    }

    switch (message.type) {
      case MessageType.MESSAGE:
        if (!message.channel) break;
        if (
          message.author !== this.tokenData.employee_id &&
          (await this.getChannelById(message.channel))?.config?.mute === false
        )
          await this.playMessageSound();
        this.chatMessagesStore.addMessage(message, message.channel);
        break;
      case MessageType.EMPLOYEE_STATUS:
        this.channelStore.setOnline(
          message.employees_online.map((employee) => employee.id),
        );
        this.channelStore.setLastSeen(message.employees_offline);
        break;
      case MessageType.UPDATE:
        if (!message.channel) break;
        this.chatMessagesStore.updateMessage(message, message.channel);
        break;
      case MessageType.DELETE:
        if (!message.channel) break;
        this.chatMessagesStore.deleteMessage(message.id, message.channel);
        break;
      case MessageType.CHANNEL_INIT:
        this.channelStore.addChannel(message.message);
        break;
      case MessageType.CHANNEL_UPDATE:
        this.channelStore.updateChannel(message.message);
        break;
      case MessageType.CHANNEL_DELETE:
        if (!message.message.id) break;
        this.channelStore.deleteChannel(message.message.id);
        this.chatMessagesStore.deleteMessages(message.message.id);
        break;
      case MessageType.TYPING:
        this.setTyping(message.channel, message.author);
        break;
      case MessageType.FILE_UPLOAD:
        const attachment = await this.attachmentsApi.getAttachment(
          message.attachmentId,
        );
        if (attachment) {
          this.chatMessagesStore.updateMessageAttachment(
            attachment,
            message.channel,
          );
        }
        break;
      case MessageType.UNREAD:
        if (message.unreadMessagesCount === 0) {
          void this.notificationDismissal.closeChannelNotifications(
            message.channelId,
          );
        }
        const messageChannel = this.channels().find(
          (c) => c.id === message.channelId,
        );
        if (!messageChannel) {
          // Channel list hasn't loaded yet (startup race) or the channel
          // is brand-new — fetch it instead of dropping the event; the
          // detail response carries the current unread count.
          this.fetchChannelForUnreadEvent(message.channelId);
          break;
        }
        this.channelStore.updateChannel({
          ...messageChannel,
          unreadMessagesCount: message.unreadMessagesCount,
        });
        break;
      default:
        break;
    }
  }

  private channelFetchesInFlight = new Set<string>();

  private async fetchChannelForUnreadEvent(channelId: string): Promise<void> {
    if (this.channelFetchesInFlight.has(channelId)) return;
    this.channelFetchesInFlight.add(channelId);
    try {
      await this.channelStore.getChannelById(channelId);
    } catch (_) {
      // The channel may be inaccessible; the next getChannels() reconciles.
    } finally {
      this.channelFetchesInFlight.delete(channelId);
    }
  }

  sendTyping(channel: string, author: string) {
    this.chatWs.sendTyping({
      id: uuidv4(),
      channel,
      type: MessageType.TYPING,
      datetime: moment().toISOString(),
      author,
    });
  }

  setTyping(channel: string, author: string) {
    this.typingEvent.set({ channel, author });
    if (this.typingTimeoutId) {
      clearTimeout(this.typingTimeoutId);
      this.typingDuration += 2000;
      if (this.typingDuration > this.maxTypingDuration) {
        this.typingDuration = this.maxTypingDuration;
      }
    } else {
      this.typingDuration = 2000;
    }
    this.typingTimeoutId = setTimeout(() => {
      this.typingEvent.set(null);
      this.typingDuration = 2000;
      this.typingTimeoutId = null;
    }, this.typingDuration);
  }

  private lastMessageSoundPlayedAt = 0;
  private readonly messageSoundCooldownMs = 1500;

  private async playMessageSound() {
    const now = Date.now();
    if (now - this.lastMessageSoundPlayedAt < this.messageSoundCooldownMs) {
      return;
    }
    this.lastMessageSoundPlayedAt = now;
    try {
      const audio = new Audio('assets/sounds/notifications.mp3');
      audio.volume = 0.5;
      await audio.play();
    } catch (error) {
      console.error('Error playing sound', error);
    }
  }

  async sendEdit(message: RawChatMessage): Promise<void> {
    await this.messagesApi.patchMessage({
      id: message.id,
      content: {
        ...message.content,
        text: message.content.text,
      },
    });
    this.notifications.showSuccess('Message successfully edited');

    this.amplitude.track('chat_message_edited', {
      channel_id: this.currentChannelId(),
      message_id: message.id,
    });
  }

  async sendReaction(message: ChatMessage, emoji: string): Promise<void> {
    await this.messagesApi.sendReaction(message.id, emoji);
    this.amplitude.track('chat_reaction_added', {
      channel_id: this.currentChannelId(),
      message_id: message.id,
    });
  }

  markMessageAsViewed(message: ChatMessage): void {
    this.chatWs.sendViewed(message);
  }

  async sendDelete(message: ChatMessage): Promise<void> {
    if (!message.channel) return;
    await this.messagesApi.deleteMessage(message.id);
    this.chatMessagesStore.deleteMessage(message.id, message.channel);
    this.notifications.showSuccess('Message successfully deleted');

    this.amplitude.track('chat_message_deleted', {
      channel_id: message.channel,
      message_id: message.id,
    });
  }

  async toggleMute() {
    const currentChannel = this.currentChannel();
    if (!currentChannel?.config?.id) return;
    const config = await this.channelsApi.updateChannelConfig(
      currentChannel.config.id,
      { mute: !currentChannel.config.mute },
    );
    this.channelStore.updateChannel({
      ...currentChannel,
      config,
    });
  }
}
